import { motion } from "framer-motion";
import { Sparkle, Info, Check, Star, Plus } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import ZoneSection from "@/components/ZoneSection";
import Seo from "@/components/Seo";

const formulas = [
  {
    id: "start",
    name: "PIXORA Start",
    price: 149,
    tagline: "Formule digitale",
    features: [
      "Galerie photos digitale",
      "20 cadres photo personnalisables",
      "Accessoires photo",
      "Accessoires fun",
      "Flash LED professionnel",
      "Installation et reprise incluses"
    ],
    note: "Cette formule est 100 % digitale : aucune impression incluse.",
    options: []
  },
  {
    id: "signature",
    name: "PIXORA Signature",
    price: 199,
    tagline: "Le meilleur équilibre",
    features: [
      "Galerie photos digitale",
      "20 cadres photo personnalisables",
      "Accessoires photo",
      "Accessoires fun",
      "Flash LED professionnel",
      "50 impressions haute qualité incluses",
      "Installation et reprise incluses"
    ],
    options: [
      { id: "impressions_50", name: "50 impressions supp.", price: 39 }
    ],
    popular: true
  },
  {
    id: "prestige",
    name: "PIXORA Prestige",
    price: 279,
    tagline: "Une expérience premium",
    features: [
      "Galerie photos digitale",
      "20 cadres photo personnalisables",
      "Accessoires photo",
      "Accessoires fun",
      "Flash LED professionnel",
      "150 impressions haute qualité incluses",
      "Installation et reprise incluses"
    ],
    options: [
      { id: "livre_or", name: "Livre d'or", price: 29 }
    ]
  },
  {
    id: "wedding",
    name: "PIXORA Wedding",
    price: 389,
    tagline: "La formule mariage complète",
    features: [
      "Galerie photos digitale",
      "Cadre photo entièrement personnalisé",
      "Flash LED professionnel",
      "350 impressions haute qualité incluses",
      "Livre d'or personnalisé inclus",
      "Props premium spécial mariage",
      "Installation et reprise incluses"
    ],
    options: [],
    highlight: true
  }
];

function PricingCard({ formula }) {
  const isPopular = formula.popular;
  const isHighlight = formula.highlight;
  
  return (
    <div
      className={`relative flex flex-col h-full rounded-2xl transition-all ${
        isPopular 
          ? "bg-[#0A0A0A] border-2 border-[#C9A962] shadow-[0_0_30px_rgba(201,169,98,0.3)]" 
          : "bg-[#0A0A0A] border border-[#C9A962]/20 hover:border-[#C9A962]/40"
      }`}
      data-testid={`pricing-card-${formula.id}`}
    >
      {/* Badge Populaire */}
      {isPopular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
          <div className="flex items-center gap-1 bg-gradient-to-r from-[#D4BA7A] to-[#C9A962] text-black text-xs font-bold px-4 py-2 rounded-full shadow-lg">
            <Star size={14} weight="fill" />
            Populaire
          </div>
        </div>
      )}
      
      {/* Badge Mariage */}
      {isHighlight && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
          <div className="flex items-center gap-1 bg-gradient-to-r from-[#D4BA7A] to-[#C9A962] text-black text-xs font-bold px-4 py-2 rounded-full shadow-lg">
            💍 Mariage
          </div>
        </div>
      )}
      
      <div className="p-6 flex flex-col flex-grow">
        {/* Header */}
        <div className="text-center mb-6 pt-2">
          <h3 className="text-lg font-bold text-white mb-1">{formula.name}</h3>
          {formula.tagline && (
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A962]/80 mb-2">{formula.tagline}</p>
          )}
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-4xl font-extrabold gradient-text">{formula.price}</span>
            <span className="text-xl gradient-text">€</span>
          </div>
        </div>
        
        {/* Features */}
        <ul className="space-y-2 mb-4 flex-grow">
          {formula.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2">
              <div className="mt-1 w-4 h-4 rounded-full bg-gradient-to-br from-[#C9A962] to-[#A88B4A] flex items-center justify-center flex-shrink-0">
                <Check size={10} weight="bold" className="text-black" />
              </div>
              <span className="text-[#B8A88A] text-sm leading-snug break-words">{feature}</span>
            </li>
          ))}
        </ul>

        {/* Special note */}
        {formula.note && (
          <p className="text-[11px] italic text-[#8A7A5A] mb-4 leading-snug">
            {formula.note}
          </p>
        )}
        
        {/* Options disponibles avec prix */}
        {formula.options && formula.options.length > 0 && (
          <div className="mb-6 p-3 rounded-xl bg-[#000000] border border-[#C9A962]/30">
            <p className="text-xs text-[#C9A962] font-semibold mb-2 uppercase tracking-wider">Options</p>
            <div className="space-y-2">
              {formula.options.map((option) => (
                <div key={option.id || option.name} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Plus size={12} className="text-[#C9A962] flex-shrink-0" weight="bold" />
                    <span className="text-[#B8A88A] text-sm truncate">{option.name}</span>
                  </div>
                  <span className="text-white font-semibold text-sm flex-shrink-0">+{option.price}€</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Spacer pour les cartes sans options ni note */}
        {(!formula.options || formula.options.length === 0) && !formula.note && (
          <div className="mb-6" />
        )}
        
        {/* CTA - Toujours en bas */}
        <div className="mt-auto">
          <Link to="/reservation" data-testid={`pricing-cta-${formula.id}`}>
            <button 
              className={`w-full py-3 rounded-full font-bold transition-all text-sm ${
                isPopular
                  ? "bg-gradient-to-r from-[#D4BA7A] to-[#C9A962] text-black shadow-[0_0_20px_rgba(201,169,98,0.4)] hover:shadow-[0_0_30px_rgba(201,169,98,0.6)]" 
                  : "border border-[#C9A962] text-[#C9A962] hover:bg-[#C9A962]/10"
              }`}
            >
              Choisir cette formule
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function TarifsParticuliers() {
  return (
    <div className="pt-24 md:pt-32 lg:pt-36" data-testid="tarifs-particuliers-page">
      <Seo
        title="Tarifs Photobooth Particuliers Bourgoin-Jallieu | Pixora"
        description="Tarifs transparents pour la location de photobooth à Bourgoin-Jallieu et en Nord-Isère : 4 formules de 149€ à 389€. Mariages, anniversaires, soirées."
        path="/tarifs-particuliers"
      />
      {/* Hero */}
      <section className="py-16 md:py-24 bokeh-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <Sparkle size={20} className="text-[#C9A962]" weight="fill" />
              <span className="text-sm uppercase tracking-[0.2em] text-[#B8A88A]">
                Particuliers
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white mb-6">
              Nos <span className="gradient-text">Formules</span>
            </h1>
            <p className="text-lg text-[#B8A88A] max-w-2xl mx-auto">
              Des formules adaptées à tous vos événements personnels
            </p>
          </motion.div>
          
          {/* Pricing cards - 4 colonnes uniformes */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
          >
            {formulas.map((formula) => (
              <PricingCard key={formula.id} formula={formula} />
            ))}
          </motion.div>
          
          {/* Info box */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-2xl p-6 max-w-3xl mx-auto border border-[#C9A962]/30"
            data-testid="info-box"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#C9A962]/20 flex items-center justify-center flex-shrink-0">
                <Info size={20} className="text-[#C9A962]" />
              </div>
              <div>
                <h4 className="text-white font-semibold mb-2">Important</h4>
                <ul className="text-[#B8A88A] text-sm space-y-1">
                  <li>• Livraison incluse jusqu'à 20 km de Bourgoin-Jallieu</li>
                  <li>• Au-delà : 0,50€/km supplémentaire (x4 trajets)</li>
                  <li>• Durée de location : 4 heures minimum</li>
                  <li>• Acompte de 30% à la réservation</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      <ZoneSection />
    </div>
  );
}
